import React, { useMemo } from "react";
import { ArrowRight, BadgeCheck, CalendarClock, Shapes } from "lucide-react";
import { useNavigate } from "react-router-dom";

import { mock } from "@/mock";
import { cn } from "@/lib/utils";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

import { Reveal } from "@/components/site/SiteLayout";

function Glass({ className, children }) {
  return (
    <div
      className={cn(
        "glass rounded-3xl shadow-[0_18px_50px_rgba(0,0,0,0.4)]",
        "transition-shadow duration-200 hover:shadow-[0_0_26px_rgba(0,194,255,0.3)]",
        className,
      )}
    >
      {children}
    </div>
  );
}

export default function Pricing() {
  const navigate = useNavigate();
  const plans = useMemo(() => mock.pricing, []);

  return (
    <div className="mx-auto max-w-[1200px] px-5 py-16">
      <Reveal delay={0}>
        <div className="inline-flex items-center gap-2 rounded-full border border-primary/30 bg-black/40 px-4 py-2 text-xs text-white/70">
          <span className="h-1.5 w-1.5 rounded-full bg-primary" />
          Pricing
        </div>
      </Reveal>

      <Reveal delay={90}>
        <h1 className="mt-5 text-3xl font-semibold tracking-tight text-white md:text-4xl">
          Transparent starting points—flexible execution
        </h1>
        <p className="mt-4 max-w-2xl text-base leading-relaxed text-white/70">
          Every build is different. These tiers give a realistic baseline; we finalize scope after discovery.
        </p>
      </Reveal>

      <Separator className="my-12 bg-white/10" />

      <div className="grid gap-4 lg:grid-cols-3">
        {plans.map((p, idx) => (
          <Reveal key={p.id} delay={80 + idx * 80}>
            <Card
              className={cn(
                "relative overflow-hidden rounded-3xl border border-primary/20 bg-black/40 text-white glass",
                p.highlight
                  ? "shadow-[0_18px_60px_rgba(0,0,0,0.55)]"
                  : "shadow-[0_16px_50px_rgba(0,0,0,0.45)]",
              )}
            >
              {p.highlight ? (
                <div className="pointer-events-none absolute -top-24 right-[-120px] h-[260px] w-[260px] rounded-full bg-[radial-gradient(closest-side,rgba(0,194,255,0.20),transparent_65%)]" />
              ) : null}

              <CardHeader>
                <CardTitle className="flex items-center justify-between gap-4 text-white">
                  <span>{p.name}</span>
                  {p.highlight ? (
                    <span className="rounded-full border border-primary/35 bg-primary/15 px-3 py-1 text-xs text-white">
                      Most chosen
                    </span>
                  ) : null}
                </CardTitle>
                <div className="mt-3 text-2xl font-semibold tracking-tight">
                  {p.price}
                </div>
                <div className="mt-2 text-sm text-white/70">{p.blurb}</div>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {p.includes.map((i) => (
                    <div key={i} className="flex items-start gap-3">
                      <div className="mt-1 grid h-6 w-6 place-items-center rounded-full border border-primary/25 bg-black/55">
                        <BadgeCheck className="h-4 w-4 text-primary" />
                      </div>
                      <div className="text-sm text-white/75">{i}</div>
                    </div>
                  ))}
                </div>

                <div className="mt-6 rounded-2xl border border-white/10 bg-black/40 p-4">
                  <div className="text-xs text-white/55">Best for</div>
                  <div className="mt-1 text-sm text-white/80">{p.bestFor}</div>
                </div>

                <Button
                  onClick={() => navigate("/contact#form")}
                  className={cn(
                    "mt-6 h-[52px] w-full rounded-xl",
                    p.highlight
                      ? "bg-primary text-black btn-glow hover:bg-primary/90"
                      : "bg-black/40 text-white border border-primary/25 hover:bg-primary/10",
                  )}
                  variant={p.highlight ? "default" : "secondary"}
                >
                  Get a Quote
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </CardContent>
            </Card>
          </Reveal>
        ))}
      </div>

      <Separator className="my-12 bg-white/10" />

      <Reveal delay={200}>
        <div className="grid gap-6 lg:grid-cols-[1fr_0.9fr]">
          <Glass className="p-8">
            <div className="flex items-start justify-between gap-6">
              <div>
                <div className="text-xs tracking-[0.22em] text-white/55">
                  ENGAGEMENT MODELS
                </div>
                <div className="mt-3 text-2xl font-semibold tracking-tight text-white">
                  Choose how you want to work
                </div>
                <p className="mt-4 text-sm leading-relaxed text-white/70">
                  We’ll recommend the model that fits your timeline, scope volatility, and risk tolerance.
                </p>
              </div>
              <div className="grid h-11 w-11 place-items-center rounded-2xl border border-white/10 bg-black/55">
                <Shapes className="h-5 w-5 text-primary" />
              </div>
            </div>

            <div className="mt-7">
              <Accordion type="single" collapsible>
                {[{
                  q: "Project-based",
                  a: "Best when the scope is clear and you want a fixed timeline. We define deliverables + acceptance criteria and execute end-to-end.",
                }, {
                  q: "Monthly retainer",
                  a: "Best for continuous iteration and long-term partnership. You get a reserved capacity and predictable planning cadence.",
                }, {
                  q: "Hourly",
                  a: "Best for short tasks, audits, or advisory. We track time transparently and share weekly progress.",
                }].map((f) => (
                  <AccordionItem key={f.q} value={f.q} className="border-white/10">
                    <AccordionTrigger className="text-white/90 hover:no-underline">
                      {f.q}
                    </AccordionTrigger>
                    <AccordionContent className="text-white/70">
                      {f.a}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          </Glass>

          <Glass className="relative overflow-hidden p-8">
            <div className="pointer-events-none absolute -top-24 right-[-90px] h-[280px] w-[280px] rounded-full bg-[radial-gradient(closest-side,rgba(0,194,255,0.18),transparent_65%)]" />
            <div className="pointer-events-none absolute -bottom-32 left-[-120px] h-[340px] w-[340px] rounded-full bg-[radial-gradient(closest-side,rgba(255,255,255,0.10),transparent_65%)]" />

            <div className="relative">
              <div className="flex items-center gap-3">
                <div className="grid h-10 w-10 place-items-center rounded-2xl border border-white/10 bg-black/55">
                  <CalendarClock className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <div className="text-sm font-medium text-white">Discovery call</div>
                  <div className="text-xs text-white/55">15–30 minutes</div>
                </div>
              </div>

              <div className="mt-6 text-sm leading-relaxed text-white/70">
                We’ll ask a few focused questions and propose a clean scope.
                You’ll get a written plan with milestones.
              </div>

              <Button
                onClick={() => navigate("/contact#form")}
                className="mt-7 h-[52px] w-full rounded-xl bg-primary text-black btn-glow hover:bg-primary/90"
              >
                Book via quote form
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>

              <div className="mt-6 text-xs text-white/55">
                These prices are placeholders (mock) until you share your real pricing policy.
              </div>
            </div>
          </Glass>
        </div>
      </Reveal>
    </div>
  );
}
